import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { NumberInput } from "components/forms/Number-input/NumberInput";

export interface ListOption {
  label: string;
  value: string;
}

export interface ListItem {
  id: number | string;
  value: string;
  percentage: number;
  is_deleted: boolean;
}

interface Props {
  charName: string;
  options: ListOption[];
  isMultiple?: boolean;
  /** Current data rows for the characteristic (one for single, many for multiple). */
  initialItems: { id: number | string; value: string; percentage: number }[];
  viewOnly?: boolean;
  onApply: (items: ListItem[]) => void | Promise<void>;
  onCancel: () => void;
}

const nextId = (items: ListItem[]): string =>
  String(items.reduce((max, item) => Math.max(max, Number(item.id) || 0), 0) + 1);

/**
 * Editor for a `list` client characteristic. Mirrors the deal SingleCharForm: branches on is_multiple
 * into a single Autocomplete or a multi-row (value select + percentage) editor. Options are provided by
 * the caller (sourced by name for the system chars, whose bar response lacks list_options).
 */
export const ClientListCharForm: React.FC<Props> = ({
  charName,
  options,
  isMultiple,
  initialItems,
  viewOnly,
  onApply,
  onCancel
}) => {
  // -------- Single mode --------
  const initialValue = initialItems[0]?.value ?? null;
  const [selected, setSelected] = useState<ListOption | null>(
    () => options.find((option) => option.value === initialValue) ?? null
  );
  const isDirty = useMemo(() => selected?.value !== initialValue, [selected, initialValue]);

  const applySingle = () => {
    if (!selected) return;
    onApply([
      {
        id: initialItems[0]?.id ?? "1",
        value: selected.value,
        percentage: initialItems[0]?.percentage ?? 100,
        is_deleted: false
      }
    ]);
  };

  // -------- Multiple mode --------
  const [items, setItems] = useState<ListItem[]>(() => initialItems.map((item) => ({ ...item, is_deleted: false })));
  const visibleItems = items.filter((item) => !item.is_deleted);
  const total = visibleItems.reduce((acc, item) => acc + item.percentage, 0);

  const updateItem = useCallback((id: ListItem["id"], patch: Partial<ListItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }, []);

  const addItem = useCallback(() => {
    setItems((prev) => [...prev, { id: nextId(prev), value: "", percentage: 0, is_deleted: false }]);
  }, []);

  const removeItem = useCallback((id: ListItem["id"]) => {
    setItems((prev) =>
      typeof id === "number"
        ? prev.map((item) => (item.id === id ? { ...item, is_deleted: true, percentage: 0 } : item))
        : prev.filter((item) => item.id !== id)
    );
  }, []);

  const spreadEqually = useCallback(() => {
    setItems((prev) => {
      const visible = prev.filter((item) => !item.is_deleted);
      if (!visible.length) return prev;
      const share = Number((100 / visible.length).toFixed(2));
      const delta = Number((100 - share * visible.length).toFixed(2));
      let first = true;
      return prev.map((item) => {
        if (item.is_deleted) return item;
        const percentage = first ? share + delta : share;
        first = false;
        return { ...item, percentage };
      });
    });
  }, []);

  // Exclude values picked in other visible rows so a value can't be selected twice.
  const optionsFor = (currentValue: string): ListOption[] =>
    options.filter((option) => !visibleItems.some((item) => item.value === option.value && item.value !== currentValue));

  const multipleDisabled =
    visibleItems.length > 0 && (total !== 100 || visibleItems.some((item) => !item.value));

  const applyMultiple = () => onApply(items);

  if (!isMultiple) {
    return (
      <Stack sx={{ p: "16px", gap: "12px", minWidth: "260px", width: "520px", maxHeight: 500 }}>
        <Autocomplete
          options={options}
          value={selected}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(a, b) => a.value === b.value}
          disabled={viewOnly}
          onChange={(_, option) => setSelected(option)}
          renderInput={(params) => <TextField {...params} label={charName} size="small" />}
        />
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap="10px">
          <Button variant="outlined" onClick={onCancel}>
            {viewOnly ? "Close" : "Cancel"}
          </Button>
          {!viewOnly && (
            <Button variant="contained" disabled={!selected || !isDirty} onClick={applySingle}>
              Apply
            </Button>
          )}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack sx={{ p: "16px", gap: "16px", width: "520px", maxHeight: 500 }} justifyContent="space-between">
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "16px", color: "#496682" }}>Total percentages should add up to 100%</Typography>
        {!viewOnly && (
          <Button size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={spreadEqually}>
            Spread Equally
          </Button>
        )}
      </Stack>

      <Stack sx={{ gap: "16px", overflow: "auto", py: "5px" }}>
        {visibleItems.map((item) => (
          <Stack key={item.id} sx={{ flexDirection: "row", alignItems: "center", gap: "16px" }}>
            <Autocomplete
              fullWidth
              options={optionsFor(item.value)}
              value={options.find((option) => option.value === item.value) ?? null}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              disabled={viewOnly}
              onChange={(_, option) => updateItem(item.id, { value: option?.value ?? "" })}
              renderInput={(params) => <TextField {...params} label={charName} size="small" />}
            />
            <NumberInput
              size="small"
              label="Share"
              sx={{ width: 120 }}
              value={item.percentage}
              disabled={viewOnly}
              onChange={(e) => updateItem(item.id, { percentage: Number(e.target.value) })}
              numberInputProps={{ suffix: "%", allowNegative: false, isNumericString: true, decimalScale: 2 }}
            />
            {viewOnly ? null : (
              <IconButton size="sm" sx={{ bgcolor: "#f5f6f7" }} onClick={() => removeItem(item.id)}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        ))}
        {!viewOnly && (
          <IconButton onClick={addItem}>
            <CloseIcon sx={{ transform: "rotate(45deg)", bgcolor: "#f5f6f7" }} />
          </IconButton>
        )}
      </Stack>

      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: "15px", color: "#496682" }}>Total: {total}%</Typography>
        <Stack direction="row" gap="8px">
          <Button variant="outlined" onClick={onCancel}>
            {viewOnly ? "Close" : "Cancel"}
          </Button>
          {!viewOnly && (
            <Button variant="contained" disabled={multipleDisabled} onClick={applyMultiple}>
              Apply
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
